import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle, Lock } from "lucide-react";
import { authApiService } from "../../services/api/authService";
import { toUserFacingError } from "../../utils/userFacingError";

const ResetPasswordPage: React.FC = () => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This reset link is missing a token.");
    }
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!companySlug) {
      setError("Company portal is required.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await authApiService.resetPassword(
        token,
        password,
        companySlug
      );
      if (!response.success) {
        throw new Error(response.message || "Failed to reset password");
      }
      setIsSuccess(true);
      window.setTimeout(() => {
        navigate(`/client/${companySlug}/admin/login`);
      }, 1500);
    } catch (err) {
      setError(
        toUserFacingError(
          err,
          "Unable to reset your password right now. Please try again later."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Set your password</h1>
        <p className="text-sm text-gray-600 mb-6">
          Create a password for your employer dashboard account.
        </p>

        {isSuccess ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 flex">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="ml-3 text-sm text-green-800">
              Password updated. Redirecting to login...
            </p>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-800">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <div className="mt-1 relative">
                <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Re-enter password"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !token}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: "#0076b5" }}
            >
              {isSubmitting ? "Saving..." : "Save password"}
            </button>
          </form>
        )}

        <p className="mt-6 text-sm text-gray-600 text-center">
          <Link
            to={companySlug ? `/client/${companySlug}/admin/login` : "/login"}
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
